import './configuration.less';

import React, { useCallback, useEffect } from 'react';
import { Observer, observer } from 'mobx-react';
import b_ from 'b_';
import { makeAutoObservable } from 'mobx';

import { AntRadioGroup } from 'controls/react/ant/antRadio';
import { getTargetTypes } from 'areas/assets/monitors/helpers';
import { MonitorBase } from './monitorBase';
import { MonitorTarget } from './monitorTarget';
import { AntInput } from 'controls/react/ant/antInput';
import { AntInputNumber } from 'controls/react/ant/antInputNumber';
import { Toolbar, ToolbarItemPosition } from 'controls/react/layout/toolbar';
import { AntButton } from 'controls/react/ant/antButton';
import { FormEntryNew, HelpIcon } from 'controls/react/form/formEntryNew';
import { getSeverityDataList, Severity } from 'framework/entities/healthData';
import { SeverityLabel } from 'controls/react/severityIndicator';
import { AntSelect } from 'controls/react/ant/antSelect';
import { AssetsRouter } from 'areas/assets/bundleDescription';
import { AssetGroupRouter } from 'areas/assetgroups/bundleDescription';
import type { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useAssetGroups } from 'controls/react/assetGroupMultiSelect';
import { i18n } from 'core/localization';
import { useAssetGroupAssets } from 'areas/assetgroups/api';
import {
	AddAssetGroupButton,
	AssetGroupMonitorEditProps,
} from 'areas/monitors/components/add-asset-group-button/addAssetGroupButton';
import { MobxManager } from 'framework/mobx-integration';
import { Link as LinkModel } from 'core/react/links';
import { useStore } from 'core/react/useStore';
import { Link } from 'controls/react/link';
import { MonitorType } from 'areas/assets/monitors/monitorType';
import { DataHolder } from 'framework/data-loading/dataHolder';
import { Asset } from 'framework/entities/asset';
import { AssetsDataset, getAssetGroupAssets, getAssets } from 'areas/assets/api';
import Configuration from 'configuration';

const entryDisabled = <TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) =>
	props.editable === false;

export type MonitorConfigurationEntryProps<
	TMonitor extends MonitorBase<TMonitor>,
	TKeys extends keyof MonitorBase<TMonitor> = keyof MonitorBase<TMonitor>,
> = {
	monitor: Pick<MonitorBase<TMonitor>, TKeys>;
	target?: MonitorTarget;
	editable?: boolean;
	systemType?: string;
	size?: SizeType;
	width?: number;
};

export const MonitorMeasuringAssetType = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) => {
		return (
			<FormEntryNew label={i18n('target-type')} model={props.target} modelField={'type'}>
				<AntRadioGroup dataList={getTargetTypes()} buttonStyle={'solid'} disabled={entryDisabled(props)} />
			</FormEntryNew>
		);
	},
);

export const MonitorForCreatedAssetGroupKey = 'monitor-for-created-asset-group';
export const CreatedAssetGroupForCachedMonitorKey = 'created-asset-group-for-cached-monitor';
export type MonitorAssetGroupProps<TMonitor extends MonitorBase<TMonitor>> = MonitorConfigurationEntryProps<TMonitor> &
	AssetGroupMonitorEditProps<TMonitor> & { tooltip?: string };

export const MonitorAssetGroup = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorAssetGroupProps<TMonitor>) => {
		//const groups = apiFetchObservable(getAssetGroups(requestOptions));
		const [groups, loading] = useAssetGroups(
			(typeof props.monitor == 'function' ? props.monitor() : props.monitor).accountId,
		);

		const onAdded = useCallback(
			(targetId: string) => {
				props.target.targetId = targetId;
			},
			[props.target.targetId],
		);

		const options = React.useMemo(() => {
			return groups.map((x) => ({
				value: x.id,
				label: (
					<Link enabled={x.id == props.target.targetId} url={AssetGroupRouter.details(x.id)}>
						{x.name}
					</Link>
				),
				searchField: x.name,
			}));
		}, [groups, props.target.targetId]);

		return (
			<FormEntryNew
				label={i18n('Asset Group')}
				model={props.target}
				modelField={'targetId'}
				disabled={entryDisabled(props)}
				className={'asset-group-select'}
				headerAdditionalContent={props.tooltip ? <HelpIcon tooltip={props.tooltip} /> : null}
			>
				<AntSelect options={options} optionFilterProp={'searchField'} loading={loading} />
				{!entryDisabled(props) && (
					<AddAssetGroupButton
						monitor={props.monitor}
						onChange={onAdded}
						ignoreType={props.ignoreType}
						onClick={props.onClick}
					/>
				)}
			</FormEntryNew>
		);
	},
);

export class MonitorAssetGroupWithLinkStore {
	model: MonitorTarget;
	mobx = new MobxManager();
	constructor(private link: LinkModel<string>) {
		this.link = link;
		makeAutoObservable(this);
		this.mobx.reaction(
			() => this.model.targetId,
			() => {
				this.link.update(this.model.targetId);
			},
		);
		this.link.changed(() => {
			this.model.targetId = this.link.value;
		});
		this.model = { targetId: this.link.value } as MonitorTarget;
	}

	destroy() {
		this.mobx.destroy();
	}
}
export const MonitorAssetGroupWithLink = observer(
	<TMonitor extends MonitorBase<TMonitor>>({
		link,
		monitor,
		onAddClick,
		tooltip,
		disabled,
	}: {
		link: LinkModel<string>;
		monitor: TMonitor;
		onAddClick: () => void;
		tooltip?: string;
		disabled?: boolean;
	}) => {
		const store = useStore(() => new MonitorAssetGroupWithLinkStore(link));
		return (
			<MonitorAssetGroup
				monitor={monitor}
				target={store.model}
				ignoreType={true}
				onClick={onAddClick}
				tooltip={tooltip}
				editable={!disabled}
			/>
		);
	},
);

export type MonitorAssetBaseProps<TMonitor extends MonitorBase<TMonitor>> = MonitorConfigurationEntryProps<TMonitor> & {
	agentsOnly?: boolean;
	size?: SizeType;
	width?: number;
	field: 'targetId' | 'templateAssetId';
	monitorType?: MonitorType;
};

export class MonitorAssetBaseStore<TMonitor extends MonitorBase<TMonitor>> {
	assetsDataHolder: DataHolder<Asset[]>;
	props: MonitorAssetBaseProps<TMonitor>;

	constructor() {
		makeAutoObservable(this);
	}

	init() {
		this.assetsDataHolder = new DataHolder<Asset[]>({
			request: () => this.getAssetsRequest(),
			defaultValue: [],
		});
	}

	getAssetsRequest() {
		if (!this.props) return null;

		if (!this.props.monitor.accountId) return null;

		if (this.props.field == 'templateAssetId') {
			if (!this.props.target.targetId) return null;

			return getAssetGroupAssets({
				accountId: this.props.monitor.accountId,
				assetGroupId: this.props.target.targetId,
				agentsOnly: this.props.agentsOnly,
				systemType: this.props.systemType,
			});
		} else {
			const config = Configuration.installedMonitorSettings as {
				[id: string]: { sharedAgents: boolean };
			};

			return getAssets({
				agentsOnly: this.props.agentsOnly,
				shared: config[this.props.monitorType]?.sharedAgents ?? false,
				dataset: AssetsDataset.Lite,
				accountId: this.props.monitor.accountId,
				systemType: this.props.systemType,
			});
		}
	}

	get options() {
		return (
			this.assetsDataHolder?.data?.map((x) => ({
				value: x.id,
				label: (
					<Observer>
						{() => (
							<Link enabled={x.id == this.props.target[this.props.field]} url={AssetsRouter.details(x.id)}>
								{x.name}
							</Link>
						)}
					</Observer>
				),
				searchField: x.name,
			})) ?? []
		);
	}

	destroy() {}
}

export const MonitorAssetBase = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorAssetBaseProps<TMonitor>) => {
		const store = useStore(() => new MonitorAssetBaseStore(), {
			props: props,
		});

		return (
			<FormEntryNew
				label={props.field == 'templateAssetId' ? i18n('Template Asset') : i18n('Asset')}
				model={props.target}
				modelField={props.field}
				disabled={props.editable === false}
			>
				<AntSelect
					size={props.size}
					width={props.width}
					disabled={!store.assetsDataHolder?.loaded}
					options={store.options}
					loading={store.assetsDataHolder?.loading}
				/>
			</FormEntryNew>
		);
	},
);

type MonitorAssetProps<TMonitor extends MonitorBase<TMonitor>> = Omit<MonitorAssetBaseProps<TMonitor>, 'field'>;

export const MonitorAsset = observer(<TMonitor extends MonitorBase<TMonitor>>(props: MonitorAssetProps<TMonitor>) => {
	return <MonitorAssetBase {...props} field={'targetId'} />;
});

export const MonitorTemplateAsset = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorAssetProps<TMonitor>) => {
		return <MonitorAssetBase {...props} field={'templateAssetId'} />;
	},
);

export const MonitorTimeout = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor, 'timeout'>) => {
		return (
			<FormEntryNew label={i18n('Timeout (sec)')} model={props.monitor} modelField={'timeout'}>
				<AntInputNumber />
			</FormEntryNew>
		);
	},
);

export const MonitorCvid = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) => {
		if (props.monitor.id == null) return null;

		return (
			<FormEntryNew label={i18n('CVID')} model={props.monitor} modelField={'id'}>
				<AntInput readOnly={true} />
			</FormEntryNew>
		);
	},
);

export const MonitorErrorSeverity = observer(
	<TMonitor extends MonitorBase<TMonitor>>(props: MonitorConfigurationEntryProps<TMonitor>) => {
		return (
			<FormEntryNew label={i18n('Monitor Error')} model={props.monitor} modelField={'monitorErrorSeverity'}>
				<AntSelect
					dataList={getSeverityDataList()}
					customRenderer={(item: { id: Severity; name: string }) => <SeverityLabel severity={item.id} />}
				/>
			</FormEntryNew>
		);
	},
);

const bConnectionTest = b_.with('monitor-connection-test');

export enum ConnectionTestState {
	Connected = 'connected',
	NotConnected = 'not-connected',
	Error = 'error',
	Connecting = 'connecting',
}

export type ConnectionTestProps = {
	indicatorPosition?: ToolbarItemPosition;
	autoConnect?: boolean;
	model: {
		connectionState: ConnectionTestState;
		connect: () => void;
		canTestConnection: boolean;
	};
};
export const ConnectionTest = observer((props: ConnectionTestProps) => {
	const model = props.model;

	useEffect(() => {
		if (props.autoConnect) {
			model.connect();
		}
	}, [model.connect, props.autoConnect]);

	return (
		<Toolbar appearance={'transparent'}>
			<span
				position={props.indicatorPosition}
				className={bConnectionTest('state', {
					[model.connectionState]: true,
				})}
			>
				{model.connectionState == ConnectionTestState.Connected && i18n('Connected')}
				{model.connectionState == ConnectionTestState.NotConnected && i18n('Not connected')}
				{model.connectionState == ConnectionTestState.Error && i18n('Could not connect')}
			</span>

			<AntButton
				type={model.connectionState == ConnectionTestState.Connected ? 'default' : 'primary'}
				onClick={model.connect}
				disabled={!model.canTestConnection || model.connectionState == ConnectionTestState.Connected}
				loading={model.connectionState == ConnectionTestState.Connecting}
			>
				{model.connectionState == ConnectionTestState.Connecting ? i18n('Connecting...') : i18n('Connect')}
			</AntButton>
		</Toolbar>
	);
});
